/**
 * MarkWonDialog — web "Mark Won" on the lead drawer (2026-10-03, Manak).
 *
 * 🔒 MARK WON ENDS AT A SALES ORDER (a1e3a15a2). The drawer used to PATCH
 * /lead-integrations/lead-imports/:id/stage {leadStage:'won'}, which the server
 * refuses for every caller (manual_stage_change_disabled) — and which, had it
 * worked, would have been a Won with no order behind it.
 *
 * This is the web twin of the phone's Convert-to-SO sheet
 * (mobile proposals_orders_screen.dart `_convert`, convert_so_form.dart):
 *   · pick the lead's quote that has no order yet,
 *   · answer the three order terms (required for everyone),
 *   · either attach a payment proof (POST /files, then its URL) with the amount
 *     and optional UTR, or — managers only — "Won on credit (no payment)",
 *   · POST /sales-orders/from-proposal/:id. That route runs
 *     confirm-sales-order-from-proposal.ts: the order, the PaymentConfirmation
 *     and the lead/deal advance. The server still decides who may skip a proof
 *     (convert_so_without_payment_proof); the UI only mirrors it.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/utils/api';
import { markFollowUpDone, wonFollowUpPromptText } from '../admin/leadFollowUpsApi';

/** Mirror of mobile core/auth/manager_roles.dart kManagerRoles. */
const CONVERT_MANAGER_ROLES = new Set([
  'admin', 'owner', 'super_admin', 'sales_head', 'manager', 'lead_manager', 'manager_sales', 'sr_sales_manager',
]);
export function isConvertManagerRole(role: string | null | undefined): boolean {
  return CONVERT_MANAGER_ROLES.has((role ?? '').trim().toLowerCase());
}

export interface ConvertTerms { installationIncluded: boolean | null; shippingIncluded: boolean | null; liftingIncluded: boolean | null }
const termsComplete = (t: ConvertTerms) => t.installationIncluded !== null && t.shippingIncluded !== null && t.liftingIncluded !== null;

/** Same body the phone posts (buildConvertSoBody + paymentMode). Credit carries no proof, amount or UTR. */
export function buildConvertSoBody(input: {
  mode: 'proof' | 'credit';
  terms: ConvertTerms;
  paymentProofUrl?: string | null;
  amountPaid?: number | null;
  utr?: string | null;
}): Record<string, unknown> {
  const { mode, terms } = input;
  const utr = input.utr?.trim();
  const body: Record<string, unknown> = {};
  if (mode === 'proof') {
    if (input.paymentProofUrl) body.paymentProofUrl = input.paymentProofUrl;
    if (input.amountPaid != null && Number.isFinite(input.amountPaid)) body.amountPaid = input.amountPaid;
    if (utr) body.utr = utr;
  }
  if (terms.installationIncluded !== null) body.installationIncluded = terms.installationIncluded;
  if (terms.shippingIncluded !== null) body.shippingIncluded = terms.shippingIncluded;
  if (terms.liftingIncluded !== null) body.liftingIncluded = terms.liftingIncluded;
  body.paymentMode = mode;
  return body;
}

interface QuoteRow { id: string; number?: string | null; title?: string | null; status?: string | null; total?: string | number | null; salesOrderId?: string | null; createdAt?: string | null }

const yesNo = (v: string): boolean | null => (v === 'yes' ? true : v === 'no' ? false : null);
const serverError = (err: any, fallback: string) =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

export function MarkWonDialog({ leadId, leadName, role, onClose, onWon }: {
  leadId: string;
  leadName: string;
  role: string | null | undefined;
  onClose: () => void;
  onWon: () => void;
}) {
  const isManager = isConvertManagerRole(role);
  const [quotes, setQuotes] = useState<QuoteRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [quoteId, setQuoteId] = useState('');
  const [terms, setTerms] = useState<ConvertTerms>({ installationIncluded: null, shippingIncluded: null, liftingIncluded: null });
  const [mode, setMode] = useState<'proof' | 'credit'>('proof');
  const [proof, setProof] = useState<File | null>(null);
  const [amount, setAmount] = useState('');
  const [utr, setUtr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 2026-10-07 (Manak option 1): the lead still has a follow-up date after
  // the Won → ask "Mark that follow-up as done?" before closing.
  const [followUpDue, setFollowUpDue] = useState<{ leadImportId: string; name: string | null; followUpDate: string } | null>(null);
  const [closingFollowUp, setClosingFollowUp] = useState(false);

  useEffect(() => {
    let live = true;
    api.get(`/proposals?leadId=${encodeURIComponent(leadId)}`)
      .then((res) => {
        if (!live) return;
        const rows: QuoteRow[] = Array.isArray(res.data?.data) ? res.data.data : [];
        setQuotes(rows);
      })
      .catch((err) => { if (live) setLoadError(serverError(err, 'Could not load the quotes for this lead.')); });
    return () => { live = false; };
  }, [leadId]);

  // Only a quote with no linked order can be converted (the server 409s the rest).
  const open = useMemo(
    () => (quotes ?? []).filter((q) => !(q.salesOrderId ?? '').trim())
      .sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''))),
    [quotes],
  );
  useEffect(() => {
    if (open.length && !open.some((q) => q.id === quoteId)) {
      setQuoteId(open[0].id);
      setAmount(open[0].total != null ? String(Math.round(Number(open[0].total))) : '');
    }
  }, [open, quoteId]);

  const submit = async () => {
    if (busy) return;
    const isCredit = mode === 'credit';
    if (!isCredit && !isManager && !proof) { setError('Payment proof picture is required for sales executives'); return; }
    if (!termsComplete(terms)) { setError('Answer the three order terms (Installation / Shipping / Lifting)'); return; }
    if (isCredit && !confirm('This marks the quote as Won with NO payment recorded — the amount stays as a receivable. Continue?')) return;
    setBusy(true); setError(null);
    try {
      let paymentProofUrl: string | null = null;
      if (!isCredit && proof) {
        const fd = new FormData();
        fd.append('file', proof);
        fd.append('entity', 'payment_proof');
        fd.append('proposalId', quoteId);
        const up = await api.post('/files', fd);
        paymentProofUrl = up.data?.url || up.data?.data?.url || null;
        if (!paymentProofUrl) throw new Error('Upload returned no URL');
      }
      const amountPaid = amount.trim() === '' ? null : Number(amount);
      const res = await api.post(`/sales-orders/from-proposal/${quoteId}`, buildConvertSoBody({ mode, terms, paymentProofUrl, amountPaid, utr }));
      // A 2xx with no order id is a failure (mark_won_flow.dart): never report a Won without an order.
      if (res.data?.success === false || !res.data?.data?.id) throw new Error(res.data?.error || 'The server created no sales order.');
      const due = res.data?.followUpDue;
      if (due?.leadImportId && due?.followUpDate) { setFollowUpDue(due); return; }
      onWon();
    } catch (err: any) {
      setError(serverError(err, 'Could not create the sales order.'));
      setBusy(false);
    }
  };

  const termSelect = (label: string, key: keyof ConvertTerms) => (
    <label className="block text-xs">
      <span className="mb-1 block text-[var(--text-muted)]">{label}</span>
      <select aria-label={label} className="w-full rounded-lg border px-2 py-1.5 text-sm"
        value={terms[key] === null ? '' : terms[key] ? 'yes' : 'no'}
        onChange={(e) => setTerms((t) => ({ ...t, [key]: yesNo(e.target.value) }))}>
        <option value="">Select…</option><option value="yes">Yes</option><option value="no">No</option>
      </select>
    </label>
  );

  const finishFollowUp = async (markDone: boolean) => {
    if (markDone && followUpDue) {
      setClosingFollowUp(true);
      try { await markFollowUpDone(followUpDue.leadImportId); } catch { /* the sale is saved; the row stays in Follow-ups */ }
    }
    onWon();
  };

  if (followUpDue) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
        <div role="dialog" aria-label="Sale marked Won" className="w-full max-w-md rounded-xl bg-[var(--bg-surface,#fff)] p-5 shadow-xl">
          <h3 className="text-base font-semibold">Sale marked Won</h3>
          <p className="mt-2 text-sm">{wonFollowUpPromptText(followUpDue.name || leadName, followUpDue.followUpDate)}</p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="rounded-lg border px-3 py-1.5 text-sm" disabled={closingFollowUp} onClick={() => void finishFollowUp(false)}>Keep it</button>
            <button type="button" className="rounded-lg bg-green-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={closingFollowUp} onClick={() => void finishFollowUp(true)}>Mark done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div role="dialog" aria-label="Mark won" className="w-full max-w-md rounded-xl bg-[var(--bg-surface,#fff)] p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold">Mark won — {leadName}</h3>
        <p className="mt-1 text-xs text-[var(--text-muted)]">A Won is a sales order. Pick the quote the customer accepted.</p>

        {loadError && <p className="mt-3 text-sm text-red-500">{loadError}</p>}
        {quotes === null && !loadError && <p className="mt-3 text-sm">Loading quotes…</p>}
        {quotes !== null && open.length === 0 && (
          <p className="mt-3 text-sm">This lead has no quote without a sales order. Create and send a proposal first; the lead is won when it becomes a sales order.</p>
        )}

        {open.length > 0 && (
          <div className="mt-3 space-y-3">
            <label className="block text-xs">
              <span className="mb-1 block text-[var(--text-muted)]">Quote</span>
              <select aria-label="Quote" className="w-full rounded-lg border px-2 py-1.5 text-sm" value={quoteId}
                onChange={(e) => { setQuoteId(e.target.value); const q = open.find((x) => x.id === e.target.value); setAmount(q?.total != null ? String(Math.round(Number(q.total))) : ''); }}>
                {open.map((q) => <option key={q.id} value={q.id}>{q.number || q.id} · {q.title || ''} · {q.status || ''}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {termSelect('Installation included', 'installationIncluded')}
              {termSelect('Shipping included', 'shippingIncluded')}
              {termSelect('Lifting included', 'liftingIncluded')}
            </div>
            <div className="space-y-1 text-sm">
              <label className="flex items-center gap-2"><input type="radio" name="won-mode" checked={mode === 'proof'} onChange={() => setMode('proof')} /> Payment received</label>
              {isManager && (
                <label className="flex items-center gap-2"><input type="radio" name="won-mode" checked={mode === 'credit'} onChange={() => setMode('credit')} /> Won on credit (no payment)</label>
              )}
            </div>
            {mode === 'proof' && (
              <div className="space-y-2">
                <label className="block text-xs"><span className="mb-1 block text-[var(--text-muted)]">Payment proof{isManager ? ' (optional for managers)' : ''}</span>
                  <input aria-label="Payment proof" type="file" accept="image/*,application/pdf" onChange={(e) => setProof(e.target.files?.[0] ?? null)} />
                </label>
                <label className="block text-xs"><span className="mb-1 block text-[var(--text-muted)]">Amount received</span>
                  <input aria-label="Amount received" inputMode="decimal" className="w-full rounded-lg border px-2 py-1.5 text-sm" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </label>
                <label className="block text-xs"><span className="mb-1 block text-[var(--text-muted)]">UTR (optional)</span>
                  <input aria-label="UTR" className="w-full rounded-lg border px-2 py-1.5 text-sm" value={utr} onChange={(e) => setUtr(e.target.value)} />
                </label>
              </div>
            )}
          </div>
        )}

        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="rounded-lg border px-3 py-1.5 text-sm" onClick={onClose} disabled={busy}>Cancel</button>
          {open.length > 0 && (
            <button type="button" className="rounded-lg bg-green-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50" onClick={submit} disabled={busy}>
              {busy ? 'Creating…' : 'Create sales order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
